import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import {
  formatConstraintLabel,
  formatReviewQueueLabel,
  formatSubmissionStatus,
} from './challengeUtils';
import './challengeAdmin.css';

const ADMIN_PASSWORD_STORAGE_KEY = 'share-pen-challenge-admin-password';

function formatAdminDate(value, language) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

export default function ChallengeAdminPage() {
  const { t, i18n } = useTranslation();
  const [draftPassword, setDraftPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [unlockAttempted, setUnlockAttempted] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [queueFilter, setQueueFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedProjectFilePath, setSelectedProjectFilePath] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.sessionStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY) || '';
    if (stored) {
      setAdminPassword(stored);
      setDraftPassword(stored);
    }
  }, []);

  const canAccessAdmin = useQuery(api.challenges.canAccessAdmin, {
    password: adminPassword || undefined,
  });

  const submissions = useQuery(
    api.challenges.listAdminSubmissions,
    canAccessAdmin
      ? {
          password: adminPassword,
          status: statusFilter === 'all' ? undefined : statusFilter,
          reviewQueue: queueFilter === 'all' ? undefined : queueFilter,
          limit: 250,
        }
      : 'skip',
  );

  const visibleSubmissions = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return submissions || [];
    return (submissions || []).filter((item) => {
      const haystack = [
        item.challengeTitle,
        item.challengeSlug,
        item.author.displayName,
        item.author.handle,
        item.author.email || '',
        item.excerpt,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [search, submissions]);

  useEffect(() => {
    if (!visibleSubmissions?.length) {
      setSelectedSubmissionId(null);
      return;
    }

    if (!selectedSubmissionId || !visibleSubmissions.some((item) => item.submissionId === selectedSubmissionId)) {
      setSelectedSubmissionId(visibleSubmissions[0].submissionId);
    }
  }, [selectedSubmissionId, visibleSubmissions]);

  const selectedSubmission = useQuery(
    api.challenges.getAdminSubmission,
    canAccessAdmin && selectedSubmissionId
      ? {
          password: adminPassword,
          submissionId: selectedSubmissionId,
        }
      : 'skip',
  );

  useEffect(() => {
    if (!selectedSubmission?.projectFiles?.length) {
      setSelectedProjectFilePath('');
      return;
    }

    const preferred = selectedSubmission.projectFiles.find(
      (file) => file.path === CHALLENGE_PROJECT_SUBMISSION_PATH,
    ) || selectedSubmission.projectFiles.find(
      (file) => file.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
    );
    setSelectedProjectFilePath(preferred?.path || selectedSubmission.projectFiles[0].path);
  }, [selectedSubmission?.submissionId, selectedSubmission?.projectFiles]);

  const updateSubmissionStatus = useMutation(api.challenges.adminUpdateSubmissionStatus);

  const counts = useMemo(() => {
    const rows = submissions || [];
    return {
      total: rows.length,
      submitted: rows.filter((row) => row.status === 'submitted').length,
      underReview: rows.filter((row) => row.status === 'under_review').length,
      approved: rows.filter((row) => row.status === 'approved').length,
      rejected: rows.filter((row) => row.status === 'rejected').length,
    };
  }, [submissions]);

  const selectedProjectFile = useMemo(
    () => selectedSubmission?.projectFiles?.find((file) => file.path === selectedProjectFilePath) || null,
    [selectedProjectFilePath, selectedSubmission?.projectFiles],
  );

  const handleUnlock = (event) => {
    event.preventDefault();
    setUnlockAttempted(true);
    setFeedback('');
    const nextPassword = draftPassword.trim();
    setAdminPassword(nextPassword);
    if (typeof window !== 'undefined') {
      if (nextPassword) {
        window.sessionStorage.setItem(ADMIN_PASSWORD_STORAGE_KEY, nextPassword);
      } else {
        window.sessionStorage.removeItem(ADMIN_PASSWORD_STORAGE_KEY);
      }
    }
  };

  const handleResetPassword = () => {
    setDraftPassword('');
    setAdminPassword('');
    setUnlockAttempted(false);
    setFeedback('');
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_PASSWORD_STORAGE_KEY);
    }
  };

  const handleStatusChange = async (nextStatus) => {
    if (!selectedSubmissionId) return;
    setFeedback('');
    setIsSaving(true);
    try {
      await updateSubmissionStatus({
        password: adminPassword,
        submissionId: selectedSubmissionId,
        status: nextStatus,
      });
      setFeedback(
        t('challengesRuntime.admin.updateSuccess', {
          defaultValue: `Submission moved to ${nextStatus.replace('_', ' ')}.`,
        }),
      );
    } catch (error) {
      setFeedback(error?.data?.message || error?.message || t('challengesRuntime.admin.updateFailed', {
        defaultValue: 'Could not update submission status.',
      }));
    } finally {
      setIsSaving(false);
    }
  };

  if (canAccessAdmin === false) {
    return (
      <div className="cha-shell">
        <div className="cha-login-card">
          <span className="cha-eyebrow">
            {t('challengesRuntime.admin.eyebrow', { defaultValue: 'Challenge admin' })}
          </span>
          <h1>{t('challengesRuntime.admin.loginTitle', { defaultValue: 'Unlock challenge submissions' })}</h1>
          <p>
            {t('challengesRuntime.admin.loginBody', {
              defaultValue: 'Enter the shared admin password to review community and judge challenge submissions.',
            })}
          </p>

          <form className="cha-login-form" onSubmit={handleUnlock}>
            <label className="cha-field">
              <span>{t('auth.password', { defaultValue: 'Password' })}</span>
              <input
                type="password"
                value={draftPassword}
                onChange={(event) => setDraftPassword(event.target.value)}
                placeholder="test123"
              />
            </label>
            <div className="cha-login-actions">
              <button className="btn btn-primary" type="submit">
                {t('challengesRuntime.admin.unlockAction', { defaultValue: 'Unlock admin view' })}
              </button>
            </div>
          </form>

          {unlockAttempted ? (
            <div className="cha-error">
              {t('challengesRuntime.admin.invalidPassword', {
                defaultValue: 'That password did not unlock the challenge admin view.',
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="cha-shell">
      <section className="cha-header">
        <div>
          <span className="cha-eyebrow">
            {t('challengesRuntime.admin.eyebrow', { defaultValue: 'Challenge admin' })}
          </span>
          <h1>{t('challengesRuntime.admin.title', { defaultValue: 'Challenge submissions' })}</h1>
          <p>
            {t('challengesRuntime.admin.subtitle', {
              defaultValue: 'Review every challenge entry, inspect pending judge submissions, and moderate the public queue.',
            })}
          </p>
        </div>
        <div className="cha-top-actions">
          <button className="btn btn-ghost" onClick={handleResetPassword}>
            {t('challengesRuntime.admin.lockAction', { defaultValue: 'Lock admin view' })}
          </button>
        </div>
      </section>

      <section className="cha-stats">
        <div className="cha-stat-card">
          <span>{t('challengesRuntime.admin.stats.total', { defaultValue: 'Total' })}</span>
          <strong>{counts.total}</strong>
        </div>
        <div className="cha-stat-card">
          <span>{t('challengesRuntime.submissionStatuses.submitted', { defaultValue: 'Submitted' })}</span>
          <strong>{counts.submitted}</strong>
        </div>
        <div className="cha-stat-card">
          <span>{t('challengesRuntime.submissionStatuses.underReview', { defaultValue: 'Under review' })}</span>
          <strong>{counts.underReview}</strong>
        </div>
        <div className="cha-stat-card">
          <span>{t('challengesRuntime.submissionStatuses.approved', { defaultValue: 'Approved' })}</span>
          <strong>{counts.approved}</strong>
        </div>
        <div className="cha-stat-card">
          <span>{t('challengesRuntime.submissionStatuses.rejected', { defaultValue: 'Rejected' })}</span>
          <strong>{counts.rejected}</strong>
        </div>
      </section>

      <section className="cha-toolbar">
        <input
          className="cha-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('challengesRuntime.admin.searchPlaceholder', {
            defaultValue: 'Search by challenge, author, handle, email, or excerpt',
          })}
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">{t('challengesRuntime.admin.filters.allStatuses', { defaultValue: 'All statuses' })}</option>
          <option value="submitted">{t('challengesRuntime.submissionStatuses.submitted', { defaultValue: 'Submitted' })}</option>
          <option value="under_review">{t('challengesRuntime.submissionStatuses.underReview', { defaultValue: 'Under review' })}</option>
          <option value="approved">{t('challengesRuntime.submissionStatuses.approved', { defaultValue: 'Approved' })}</option>
          <option value="rejected">{t('challengesRuntime.submissionStatuses.rejected', { defaultValue: 'Rejected' })}</option>
          <option value="draft">{t('challengesRuntime.submissionStatuses.draft', { defaultValue: 'Draft' })}</option>
        </select>
        <select value={queueFilter} onChange={(event) => setQueueFilter(event.target.value)}>
          <option value="all">{t('challengesRuntime.admin.filters.allQueues', { defaultValue: 'All queues' })}</option>
          <option value="community">{t('challengesRuntime.reviewQueues.community', { defaultValue: 'Community review' })}</option>
          <option value="judges">{t('challengesRuntime.reviewQueues.judges', { defaultValue: 'Judges review' })}</option>
        </select>
      </section>

      {feedback ? <div className="cha-feedback">{feedback}</div> : null}

      <section className="cha-grid">
        <div className="cha-list">
          <div className="cha-list-header">
            <strong>{t('challengesRuntime.admin.listTitle', { defaultValue: 'Submissions' })}</strong>
            <span>{visibleSubmissions?.length || 0}</span>
          </div>

          {submissions === undefined ? (
            <div className="cha-empty">
              {t('challengesRuntime.admin.loadingList', { defaultValue: 'Loading submissions…' })}
            </div>
          ) : visibleSubmissions.length === 0 ? (
            <div className="cha-empty">
              {t('challengesRuntime.admin.emptyList', { defaultValue: 'No submissions match the current filters.' })}
            </div>
          ) : (
            visibleSubmissions.map((item) => (
              <button
                key={item.submissionId}
                className={`cha-row ${selectedSubmissionId === item.submissionId ? 'active' : ''}`}
                onClick={() => setSelectedSubmissionId(item.submissionId)}
              >
                <div className="cha-row-top">
                  <strong>{item.challengeTitle}</strong>
                  <span>{formatSubmissionStatus(item.status, t)}</span>
                </div>
                <div className="cha-row-meta">
                  <span>@{item.author.handle}</span>
                  <span>{formatReviewQueueLabel(item.reviewQueue, t)}</span>
                </div>
                <p>{item.excerpt || t('challengesRuntime.admin.noExcerpt', { defaultValue: 'No excerpt available.' })}</p>
              </button>
            ))
          )}
        </div>

        <div className="cha-detail">
          {selectedSubmission === undefined ? (
            <div className="cha-empty">
              {t('challengesRuntime.admin.loadingDetail', { defaultValue: 'Loading submission detail…' })}
            </div>
          ) : !selectedSubmission ? (
            <div className="cha-empty">
              {t('challengesRuntime.admin.selectPrompt', { defaultValue: 'Select a submission to inspect it.' })}
            </div>
          ) : (
            <>
              <div className="cha-detail-header">
                <div>
                  <span className="cha-detail-kicker">{selectedSubmission.challengeGenre}</span>
                  <h2>{selectedSubmission.challengeTitle}</h2>
                  <p>{selectedSubmission.challengeDescription}</p>
                </div>
                <div className="cha-detail-statuses">
                  <span>{formatReviewQueueLabel(selectedSubmission.reviewQueue, t)}</span>
                  <span>{formatSubmissionStatus(selectedSubmission.status, t)}</span>
                </div>
              </div>

              <div className="cha-meta-grid">
                <div className="cha-meta-card">
                  <span>{t('challengesRuntime.admin.authorLabel', { defaultValue: 'Author' })}</span>
                  <strong>{selectedSubmission.author.displayName}</strong>
                  <small>@{selectedSubmission.author.handle}</small>
                  {selectedSubmission.author.email ? <small>{selectedSubmission.author.email}</small> : null}
                </div>
                <div className="cha-meta-card">
                  <span>{t('challengesRuntime.labels.constraint', { defaultValue: 'Constraint' })}</span>
                  <strong>{formatConstraintLabel(selectedSubmission, t)}</strong>
                </div>
                <div className="cha-meta-card">
                  <span>{t('challengesRuntime.admin.wordLineLabel', { defaultValue: 'Counts' })}</span>
                  <strong>{selectedSubmission.wordCount} / {selectedSubmission.lineCount}</strong>
                  <small>{t('challengesRuntime.admin.wordsLines', { defaultValue: 'words / lines' })}</small>
                </div>
                <div className="cha-meta-card">
                  <span>{t('challengesRuntime.submission.submittedAt', { defaultValue: 'Submitted at' })}</span>
                  <strong>{formatAdminDate(selectedSubmission.submittedAt, i18n.language)}</strong>
                  <small>{t('challengesRuntime.admin.reviewedAt', { defaultValue: 'Reviewed: {{date}}', date: formatAdminDate(selectedSubmission.reviewedAt, i18n.language) })}</small>
                </div>
              </div>

              <div className="cha-actions">
                <button className="btn btn-secondary" disabled={isSaving} onClick={() => handleStatusChange('under_review')}>
                  {t('challengesRuntime.admin.actions.markUnderReview', { defaultValue: 'Mark under review' })}
                </button>
                <button className="btn btn-secondary" disabled={isSaving} onClick={() => handleStatusChange('submitted')}>
                  {t('challengesRuntime.admin.actions.markSubmitted', { defaultValue: 'Mark submitted' })}
                </button>
                <button className="btn btn-primary" disabled={isSaving} onClick={() => handleStatusChange('approved')}>
                  {t('challengesRuntime.admin.actions.approve', { defaultValue: 'Approve' })}
                </button>
                <button className="btn btn-ghost cha-danger" disabled={isSaving} onClick={() => handleStatusChange('rejected')}>
                  {t('challengesRuntime.admin.actions.reject', { defaultValue: 'Reject' })}
                </button>
                <button className="btn btn-ghost" disabled={isSaving} onClick={() => handleStatusChange('draft')}>
                  {t('challengesRuntime.admin.actions.moveToDraft', { defaultValue: 'Move to draft' })}
                </button>
              </div>

              <div className="cha-content-card">
                <div className="cha-content-label">
                  {t('challengesRuntime.admin.submissionContent', { defaultValue: 'Submission content' })}
                </div>
                <pre>{selectedSubmission.content}</pre>
              </div>

              {selectedSubmission.projectId && selectedSubmission.projectFiles?.length ? (
                <div className="cha-content-card" style={{ marginTop: 18 }}>
                  <div className="cha-content-label">
                    {t('challengesRuntime.admin.projectReview', { defaultValue: 'Linked project workflow' })}
                  </div>
                  <div className="cha-project-meta">
                    <strong>{selectedSubmission.projectTitle}</strong>
                    <span>{selectedSubmission.projectFiles.length} {t('common.files', { defaultValue: 'files' })}</span>
                  </div>
                  <div className="cha-project-browser">
                    <div className="cha-project-files">
                      {selectedSubmission.projectFiles.map((file) => (
                        <button
                          key={file.path}
                          className={`cha-project-file ${selectedProjectFilePath === file.path ? 'active' : ''}`}
                          onClick={() => setSelectedProjectFilePath(file.path)}
                        >
                          <strong>{file.name}</strong>
                          <span>{file.path}</span>
                        </button>
                      ))}
                    </div>
                    <div className="cha-project-preview">
                      <div className="cha-project-preview-head">
                        <strong>{selectedProjectFile?.path || CHALLENGE_PROJECT_SUBMISSION_PATH}</strong>
                      </div>
                      <pre>{selectedProjectFile?.content || t('challengesRuntime.admin.emptyProjectFile', { defaultValue: 'No content in this file yet.' })}</pre>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
